import React, { useState, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import Message from '../components/Message'
import ToastMessage from '../components/ToastMessage'
import StudyEditBlockingOverlay from '../components/StudyEditBlockingOverlay'
import useStudyEditBeforeUnload from '../hooks/useStudyEditBeforeUnload'
import StudyBasic from '../components/StudyBasic'
import StudyConsent from '../components/StudyConsent'
import StudyStage from '../components/StudyStage'

import { Row, Col, Card, Button, Form } from 'react-bootstrap'

import {
  addStage,
  editStudyExceptConsent,
  listStudyDetails,
} from '../actions/studyActions'
import { getQxList } from '../../qx_module/actions/qxActions'
import { getTxPageList } from '../../tx_materials_module/actions/txMaterialsActions'
import {
  PARTI_GROUP_ADD_RESET,
  PARTI_GROUP_DELETE_RESET,
  STAGE_ADD_RESET,
  STAGE_DELETE_RESET,
  STUDY_DETAILS_RESET,
  STUDY_EDIT_CONSENT_RESET,
  STUDY_EDIT_EXCEPT_CONSENT_RESET,
} from '../constants/studyConstants'
import resetErrorState from '../utils/resetErrorState'

import hasValidLoginCredential from '../../admin_module/utils/hasValidLoginCredential'
import { getAllAccess } from '../../access_module/actions/accessActions'
import { GET_ALL_ACCESS_RESET } from '../../access_module/constants/accessConstants'
import { getJitaiMessageList, getJitaiVariableList, getJitaiDecisionRuleList, getJitaiRespList } from '../../jitai_module/actions/jitaiActions'

const StudyEditScreen = ({ match, history }) => {
  // For toast message
  const [toastMessages, setToastMessages] = useState([])

  const toastOnCloseHandler = async (uid) => {
    setToastMessages((toastMessages) =>
      toastMessages.filter((message) => message.uid !== uid)
    )
  }

  const studyId = match.params.id

  const [saveError, setSaveError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  // Which settings set is currently being edited. undefined => study default set.
  const [selectedSettingsSetId, setSelectedSettingsSetId] = useState(undefined)
  const [study, setStudy] = useState({
    _id: '',
    study_code: '',
    active_status: '',
    chin_name: '',
    contact_person_name: '',
    contact_email: '',
    lock: false,
    stage_list: [
      {
        chin_name: '',
        stage_num: 1,
        consent_info: {
          consent_needed: true,
          chin_content: '',
        },
        chin_complete_message: '',
        //participant_group was previously named as ax_version
        participant_group_list: [
          {
            name: '',
            group_num: 1,
            ema_list: [],
            diary_list: [],
            questionnaire_list: [],
            tx_package_list: [],
          },
        ],
      },
    ],
  })

  const dispatch = useDispatch()

  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const studyDetails = useSelector((state) => state.studyDetails)
  const {
    loading: loadingInitiateStudy,
    error: errorInitiateStudy,
    study: updatedStudy,
    success: successInitiateStudy,
  } = studyDetails

  ////studyEditConsent state can be altered after form in StudyConsent componet is submitted
  const studyEditConsent = useSelector((state) => state.studyEditConsent)
  const { error: errorEditConsent, success: successEditConsent } =
    studyEditConsent

  const stageAdd = useSelector((state) => state.stageAdd)
  const { error: errorStageAdd, success: successStageAdd } = stageAdd
  const stageDelete = useSelector((state) => state.stageDelete)
  const { error: errorStageDelete, success: successStageDelete } = stageDelete

  const participantGroupAdd = useSelector((state) => state.participantGroupAdd)
  const {
    error: errorParticipantGroupAdd,
    success: successParticipantGroupAdd,
  } = participantGroupAdd
  const participantGroupDelete = useSelector(
    (state) => state.participantGroupDelete
  )
  const {
    error: errorParticipantGroupDelete,
    success: successParticipantGroupDelete,
  } = participantGroupDelete

  const qxList = useSelector((state) => state.qxList)
  const {
    loading: loadingQxList,
    error: errorQxList,
    qx_list: initialQxList,
    success: successQxList,
  } = qxList

  const studyEditExceptConsent = useSelector(
    (state) => state.studyEditExceptConsent
  )
  const {
    loading: loadingEditStudy,
    error: errorEditStudy,
    success: successEditStudy,
  } = studyEditExceptConsent

  const allAccess = useSelector((state) => state.allAccess)
  const {
    loading: laodingGetAllAccess,
    error: errorGetAllAccess,
    success: successGetAllAccess,
    allAccessList,
  } = allAccess

  // JITAI data
  const jitaiMessageList = useSelector((state) => state.jitaiMessageList)
  const { 
    loading: loadingJitaiMessages,
    error: errorJitaiMessages,
    success: successJitaiMessages 
  } = jitaiMessageList
  
  const jitaiVariableList = useSelector((state) => state.jitaiVariableList)
  const { 
    loading: loadingJitaiVariables,
    error: errorJitaiVariables,
    success: successJitaiVariables 
  } = jitaiVariableList
  
  const jitaiDecisionRuleList = useSelector((state) => state.jitaiDecisionRuleList)
  const { 
    loading: loadingJitaiDecisionRules,
    error: errorJitaiDecisionRules,
    success: successJitaiDecisionRules 
  } = jitaiDecisionRuleList

  const jitaiRespListState = useSelector((state) => state.jitaiRespList)
  const { 
    loading: loadingJitaiResps,
    error: errorJitaiResps,
    success: successJitaiResps 
  } = jitaiRespListState

  // TX Materials data
  const txPageList = useSelector((state) => state.txPageList)
  const {
    loading: loadingTxPageList,
    error: errorTxPageList,
    success: successTxPageList
  } = txPageList

  useEffect(() => {
    if (!hasValidLoginCredential(userInfo, logout, window)) {
      dispatch({ type: STUDY_DETAILS_RESET })
      history.push('/login')
    } else {
      if (!loadingQxList && !errorQxList && !successQxList) {
        dispatch(getQxList())
      }

      if (!laodingGetAllAccess && !successGetAllAccess && !errorGetAllAccess) {
        dispatch(getAllAccess())
      }

      // Fetch JITAI data for the study (only once at screen level)
      if (!loadingJitaiMessages && !errorJitaiMessages && !successJitaiMessages) {
        dispatch(getJitaiMessageList(studyId))
      }
      if (!loadingJitaiVariables && !errorJitaiVariables && !successJitaiVariables) {
        dispatch(getJitaiVariableList(studyId))
      }
      if (!loadingJitaiDecisionRules && !errorJitaiDecisionRules && !successJitaiDecisionRules) {
        dispatch(getJitaiDecisionRuleList(studyId))
      }
      if (!loadingJitaiResps && !errorJitaiResps && !successJitaiResps) {
        dispatch(getJitaiRespList(studyId))
      }

      // Fetch TX materials data for the study (only once at screen level)
      if (!loadingTxPageList && !errorTxPageList && !successTxPageList) {
        dispatch(getTxPageList(studyId))
      }

      if (!loadingInitiateStudy && !errorInitiateStudy) {
        if (!successInitiateStudy) {
          resetErrorState(dispatch)
          // Put the reset for getting all access just before loading the main content again
          dispatch({ type: GET_ALL_ACCESS_RESET })
          dispatch(listStudyDetails(studyId, selectedSettingsSetId))
        } else {
          setStudy(updatedStudy)
        }
      }

      if (successEditConsent) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Consent Form Updated',
          },
        ])
        dispatch({ type: STUDY_EDIT_CONSENT_RESET })
      }

      if (successStageAdd) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Stage Added',
          },
        ])
        dispatch({ type: STAGE_ADD_RESET })
      }

      if (successStageDelete) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Stage Deleted',
          },
        ])
        dispatch({ type: STAGE_DELETE_RESET })
      }

      if (successParticipantGroupAdd) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Participant Group Added',
          },
        ])
        dispatch({ type: PARTI_GROUP_ADD_RESET })
      }

      if (successParticipantGroupDelete) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Participant Group Deleted',
          },
        ])
        dispatch({ type: PARTI_GROUP_DELETE_RESET })
      }

      if (successEditStudy) {
        window.scrollTo(0, 0)
        setIsSaving(false)
        setStudy(updatedStudy)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Study information was changed successfully',
          },
        ])
        dispatch({ type: STUDY_EDIT_EXCEPT_CONSENT_RESET })
      }
    }
  }, [
    dispatch,
    history,
    study,
    studyId,
    selectedSettingsSetId,
    userInfo,
    logout,
    updatedStudy,
    loadingInitiateStudy,
    loadingQxList,
    loadingEditStudy,
    laodingGetAllAccess,
    loadingJitaiMessages, 
    loadingJitaiVariables, 
    loadingJitaiDecisionRules,
    loadingTxPageList,
    errorInitiateStudy,
    errorQxList,
    errorEditStudy,
    errorGetAllAccess,
    errorJitaiMessages,
    errorJitaiVariables,
    errorJitaiDecisionRules,
    errorTxPageList,
    successInitiateStudy,
    successEditConsent,
    successStageAdd,
    successStageDelete,
    successQxList,
    successParticipantGroupAdd,
    successParticipantGroupDelete,
    successEditStudy,
    successGetAllAccess,
    successJitaiMessages,
    successJitaiVariables,
    successJitaiDecisionRules,
    successTxPageList,
  ])

  useEffect(() => {
    if (errorEditStudy) {
      setIsSaving(false)
    }
  }, [errorEditStudy])

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  }

  const panelStyle = {
    display: 'inline-block',
    height: '100%',
    overflowY: 'scroll',
    overflowX: 'hidden',
    marginRight: '10px',
  }

  const addStageHandler = async () => {
    if (
      window.confirm(
        'This operation will remove unsaved changes. Please save changes first if you have not done that. Are you sure you want to go ahead?'
      )
    ) {
      resetErrorState(dispatch)
      dispatch(addStage(study._id, study.settingsSetId))
    }
  }

  // Switch which settings set is being edited. Resetting study details triggers a
  // reload for the newly selected set (see the load effect above).
  const settingsSetChangeHandler = (newSetId) => {
    if (
      window.confirm(
        'Switching settings set will discard unsaved changes. Continue?'
      )
    ) {
      setSelectedSettingsSetId(newSetId)
      dispatch({ type: STUDY_DETAILS_RESET })
    }
  }

  // It is needed as diary assessment template added does not include the number of days field, which is compulsory
  const checkDiaryList = () => {
    for (let i = 0; i < study.stage_list.length; ++i) {
      for (
        let j = 0;
        j < study.stage_list[i].participant_group_list.length;
        ++j
      ) {
        let diaryList = study.stage_list[i].participant_group_list[j].diary_list
        for (let k = 0; k < diaryList.length; ++k) {
          if (
            !diaryList[k].number_of_days ||
            diaryList[k].number_of_days <= 0
          ) {
            return `Please specifiy number of days for all the diary assessments at stage ${
              i + 1
            } participant group ${j + 1}`
          }
        }
      }
    }
    return ''
  }

  const checkOptionalChapterAssignmentRules = () => {
    for (let i = 0; i < study.stage_list.length; ++i) {
      for (let j = 0; j < study.stage_list[i].participant_group_list.length; ++j) {
        const group = study.stage_list[i].participant_group_list[j]
        const settings = group.next_stage_optional_tx_package_settings || []
        for (let k = 0; k < settings.length; ++k) {
          if (!settings[k].assign_optional_tx_packages_rule) {
            return `Please select a decision rule for optional chapter assignment rule ${k + 1} at stage ${i + 1} group ${j + 1} (${group.name})`
          }
          if (!settings[k].added_optional_tx_package_list || settings[k].added_optional_tx_package_list.length === 0) {
            return `Please select at least one optional chapter for optional chapter assignment rule ${k + 1} at stage ${i + 1} group ${j + 1} (${group.name})`
          }
        }
      }
    }
    return ''
  }

  const saveHandler = async () => {
    if (isSaving || loadingEditStudy) return
    setSaveError('')
    let error = ''

    if (error.length === 0) error = checkDiaryList()
    if (error.length === 0) error = checkOptionalChapterAssignmentRules()

    if (error.length === 0) {
      setIsSaving(true)
      dispatch(editStudyExceptConsent(study._id, study))
    } else {
      setSaveError(error)
    }
  }

  const isInitialLoading = loadingInitiateStudy || laodingGetAllAccess
  const showSavingOverlay = isSaving || loadingEditStudy
  useStudyEditBeforeUnload(showSavingOverlay)

  return (
    <>
      {/* Display toast messages */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          x: '0',
          y: '0',
          height: '80vh',
          width: '80vw',
          zIndex: 10001,
        }}
      >
        {toastMessages.map((toastMessage) => (
          <ToastMessage
            key={toastMessage.uid}
            variant={toastMessage.variant}
            onClose={() => toastOnCloseHandler(toastMessage.uid)}
          >
            {toastMessage.message}
          </ToastMessage>
        ))}
      </div>

      {isInitialLoading ? (
        <StudyEditBlockingOverlay
          title='Loading study'
          message='Please wait while the study editor is loading.'
        />
      ) : (
        <>
          <div style={{ display: 'relative', height: '90vh', width: '100%' }}>
            <Row style={{ minHeight: '60px' }} className='align-items-center'>
              <Col xs='auto'>
                <h1 style={headingStyle}>Edit Study</h1>
              </Col>
              {study.settings_set_options &&
                study.settings_set_options.length > 0 && (
                  <Col xs='auto' className='ml-auto'>
                    <Form.Group
                      controlId='settingsSetSelect'
                      className='d-flex align-items-center mb-0'
                    >
                      <Form.Control
                        as='select'
                        size='sm'
                        value={study.settingsSetId || ''}
                        onChange={(e) =>
                          settingsSetChangeHandler(e.target.value)
                        }
                        style={{ width: 'auto' }}
                      >
                        {study.settings_set_options.map((opt) => (
                          <option key={opt._id} value={opt._id}>
                            {opt.name}
                            {opt.is_default ? ' (default)' : ''}
                            {opt.labels && opt.labels.length > 0
                              ? ` [${opt.labels.join(', ')}]`
                              : ''}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                )}
              <Col xs={12}>
                {errorInitiateStudy && (
                  <Message variant='danger'>{errorInitiateStudy}</Message>
                )}

                {errorEditConsent && (
                  <Message variant='danger'>{errorEditConsent}</Message>
                )}

                {errorStageAdd && (
                  <Message variant='danger'>{errorStageAdd}</Message>
                )}

                {errorStageDelete && (
                  <Message variant='danger'>{errorStageDelete}</Message>
                )}

                {errorParticipantGroupAdd && (
                  <Message variant='danger'>{errorParticipantGroupAdd}</Message>
                )}

                {errorEditStudy && (
                  <Message variant='danger'>{errorEditStudy}</Message>
                )}

                {saveError && <Message variant='danger'>{saveError}</Message>}

                {errorParticipantGroupDelete && (
                  <Message variant='danger'>
                    {errorParticipantGroupDelete}
                  </Message>
                )}
              </Col>
            </Row>

            <div
              style={{
                display: 'block',
                width: '100%',
                height: '85%',
                overflowX: 'scroll',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <div style={{ ...panelStyle, width: '300px' }}>
                <Card className='p-3 rounded'>
                  <StudyBasic study={study}></StudyBasic>
                </Card>
              </div>

              {study.stage_list.map((stage) => (
                <div
                  style={{ ...panelStyle, width: '400px' }}
                  key={stage.stage_num}
                >
                  <Card className='p-3 rounded'>
                    <StudyStage
                      studyId={study._id}
                      study={study}
                      stage={study.stage_list[stage.stage_num - 1]}
                      qxList={initialQxList}
                      key={stage.stage_num}
                      goToLogin={() => {
                        dispatch({ type: STUDY_DETAILS_RESET })
                        history.push('/login')
                      }}
                      readOnly={study.lock}
                    ></StudyStage>
                  </Card>
                </div>
              ))}

              {allAccessList.find(
                (a) =>
                  a.area === 'STUDY' &&
                  a.modifier === 'ADD' &&
                  a.task === 'STUDY_STAGE'
              ) && (
                <div style={{ ...panelStyle, width: '300px' }}>
                  <Card className='p-3 rounded'>
                    <Button onClick={addStageHandler} disabled={showSavingOverlay}>
                      Add Stage
                    </Button>
                  </Card>
                </div>
              )}
            </div>
          </div>

          {showSavingOverlay && (
            <StudyEditBlockingOverlay
              title='Saving study'
              message='Saving is in progress. Please do not close this browser window or leave this page until saving completes.'
            />
          )}
        </>
      )}

      {/* Sticky bar for saving changes */}
      {!study.lock && !isInitialLoading && !showSavingOverlay && (
        <Row
          style={{
            position: 'fixed',
            backgroundColor: 'white',
            bottom: 0,
            height: '50px',
            width: '95%',
            zIndex: 1,
          }}
        >
          <Col>
            <Button
              onClick={saveHandler}
              className='my-1 btn-success'
              style={{ float: 'left', marginLeft: '10px' }}
            >
              Save Changes
            </Button>

            {allAccessList.find(
              (a) =>
                a.area === 'STUDY' &&
                a.modifier === 'EDIT' &&
                a.task === 'STUDY_CONSENT_FORM'
            ) && <StudyConsent></StudyConsent>}
          </Col>
        </Row>
      )}
    </>
  )
}

export default StudyEditScreen
